import React, { createContext, useContext, ReactNode } from 'react';
import Cookies from 'universal-cookie';

import enTranslations from '../languages/en.json';
import frTranslations from '../languages/fr.json';
import arTranslations from '../languages/ar.json';
import esTranslations from '../languages/es.json';

type Language = 'en' | 'fr' | 'ar' | 'es'; // Ajoutez d'autres langues au besoin

const supportedLanguages: Language[] = ['en', 'fr', 'ar', 'es'];
const languageCookie = new Cookies();
const LANGUAGE_COOKIE_NAME = 'language';

const isLanguage = (value: string | null): value is Language =>
  value !== null && supportedLanguages.includes(value as Language);

const translations: Record<Language, Record<string, string>> = {
  en: enTranslations,
  fr: frTranslations,
  ar: arTranslations,
  es: esTranslations
};

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:9000";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  translate: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getCurrentUserId = () => {
  const directUserId = localStorage.getItem("userid");
  if (directUserId) return directUserId;

  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return storedUser?.id_user ? String(storedUser.id_user) : "";
  } catch {
    return "";
  }
};

const persistLanguageToDatabase = async (nextLang: Language) => {
  const idUser = getCurrentUserId();
  if (!idUser) return;

  try {
    const response = await fetch(
      `${backendUrl}/api/profile/${idUser}/update-language`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ language: nextLang }),
      },
    );

    if (!response.ok) {
      throw new Error(`Language update failed with status ${response.status}`);
    }
  } catch (error) {
    console.error("Error saving language preference:", error);
  }
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = React.useState<Language>(() => {
    const cookieLanguage = languageCookie.get(LANGUAGE_COOKIE_NAME);
    if (isLanguage(cookieLanguage)) return cookieLanguage;

    const storedLanguage = localStorage.getItem('language');
    return isLanguage(storedLanguage) ? storedLanguage : 'fr';
  });

  const setLang = (nextLang: Language) => {
    localStorage.setItem('language', nextLang);
    languageCookie.set(LANGUAGE_COOKIE_NAME, nextLang, {
      path: '/',
      maxAge: 60 * 60 * 24 * 365,
      sameSite: 'lax',
    });
    setLangState(nextLang);
    void persistLanguageToDatabase(nextLang);
  };

  const isUsableTranslation = (value?: string) =>
    typeof value === "string" && value.trim() !== "" && !/\?{2,}/.test(value);

  const translate = (key: string) => {
    const activeValue = translations[lang][key];
    const frValue = translations.fr[key];
    const enValue = translations.en[key];

    if (isUsableTranslation(activeValue)) return activeValue;
    if (isUsableTranslation(frValue)) return frValue;
    if (isUsableTranslation(enValue)) return enValue;

    return key.replace(/_/g, " ");
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslate() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslate must be used within a LanguageProvider');
  }
  return context;
}
